import { defineModule } from '@nexus/core';
import { appointmentCreated, appointmentDeleted, appointmentUpdated } from './events';

const READ = 'appointments.appointment.read';
const WRITE = 'appointments.appointment.write';
const JOIN = 'appointments.appointment.join';
// Hours of the appointments of a period for invoicing, with CSV export (docs/architecture/exports.md).
const REPORT = 'appointments.appointment.report';
// Appointment requests from e-mails to the address of the company (ADR-0061).
const MANAGE_REQUESTS = 'appointments.request.manage';

/** Contract between the module and the kernel (docs/architecture/modules.md). */
export const manifest = defineModule({
  id: 'appointments',
  name: 'appointments.name',
  description: 'appointments.description',
  icon: 'calendar',
  theme: { accent: 'blue' },
  // Appointments belong to projects and their objects; the customer comes with the project.
  dependsOn: ['projects'],
  permissions: [
    // Scope "own": appointments the person takes part in or created.
    { key: READ, scopes: ['own', 'all'] },
    // Signing oneself up or off; everybody of the company who sees the appointment.
    { key: JOIN },
    // Planning: create, change, assign people, cancel. Scope "own" as for reading.
    { key: WRITE, scopes: ['own', 'all'] },
    { key: 'appointments.appointment.delete' },
    { key: 'appointments.appointment.comment' },
    { key: REPORT },
    // Requests from e-mails: see them, take them over as appointment or decline them. Taking
    // over needs the permission to plan as well.
    { key: MANAGE_REQUESTS },
    // Catalogs of appointment types and functions.
    { key: 'appointments.settings.manage', privileged: true },
  ],
  defaultRoles: [
    {
      // Everybody sees the appointments of the company and signs up for them.
      key: 'participant',
      name: 'appointments.roles.participant',
      everyone: true,
      grants: [
        { permission: READ, scope: 'all' },
        { permission: JOIN, scope: null },
        { permission: 'appointments.appointment.comment', scope: null },
      ],
    },
    {
      key: 'planner',
      name: 'appointments.roles.planner',
      grants: [
        { permission: READ, scope: 'all' },
        { permission: WRITE, scope: 'all' },
        { permission: JOIN, scope: null },
        { permission: 'appointments.appointment.delete', scope: null },
        { permission: 'appointments.appointment.comment', scope: null },
        { permission: REPORT, scope: null },
        { permission: MANAGE_REQUESTS, scope: null },
      ],
    },
    {
      key: 'manager',
      name: 'appointments.roles.manager',
      grants: [
        { permission: 'appointments.appointment.*', scope: 'all' },
        { permission: MANAGE_REQUESTS, scope: null },
        { permission: 'appointments.settings.manage', scope: null },
      ],
    },
  ],
  navigation: [
    {
      label: 'appointments.nav.appointments',
      path: '/appointments',
      icon: 'calendar',
      permission: READ,
    },
    {
      label: 'appointments.nav.requests',
      path: '/appointments/requests',
      icon: 'inbox',
      permission: MANAGE_REQUESTS,
    },
    {
      label: 'appointments.nav.report',
      path: '/appointments/report',
      icon: 'chart-bar',
      permission: REPORT,
    },
  ],
  recordSections: [
    {
      key: 'appointments.project-appointments',
      target: 'projects.project',
      title: 'appointments.sections.appointments',
      permission: READ,
      order: 5,
    },
    {
      key: 'appointments.object-appointments',
      target: 'projects.object',
      title: 'appointments.sections.appointments',
      permission: READ,
      order: 5,
    },
  ],
  dashboard: [
    {
      key: 'appointments.mine',
      title: 'appointments.dashboard.mine',
      permission: READ,
      size: 'medium',
      path: '/appointments',
    },
  ],
  catalogs: [
    {
      key: 'appointments.types',
      label: 'appointments.catalogs.types.label',
      description: 'appointments.catalogs.types.description',
      read: READ,
    },
    {
      key: 'appointments.functions',
      label: 'appointments.catalogs.functions.label',
      description: 'appointments.catalogs.functions.description',
      read: READ,
    },
  ],
  activity: [
    {
      entity: 'appointments.appointment',
      read: READ,
      comment: 'appointments.appointment.comment',
      path: '/appointments/$id',
    },
  ],
  ai: [
    {
      // Title, day, time and place from subject and text of a mail. Mails name people; names,
      // addresses and numbers are replaced before the call.
      key: 'appointments.read-mail',
      label: 'appointments.ai.readMail',
      data: 'pseudonymized',
      entities: ['appointments.request'],
      permission: WRITE,
      model: 'small',
    },
  ],
  events: {
    publishes: [appointmentCreated.type, appointmentUpdated.type, appointmentDeleted.type],
    // Appointments at an object follow its address.
    subscribes: ['projects.object.updated'],
  },
  dataProcessing: [
    {
      entity: 'appointments.appointment',
      purpose:
        'Planung von Kunden- und Projektterminen: wer welchen Termin wahrnimmt, auch Personen ohne Konto mit Namen und optional E-Mail-Adresse, Ort, Beschreibung und Online-Link; Einladungen per E-Mail an die eingetragenen Personen (ADR-0057); Auswertung der Dauer je Termin für die Abrechnung, nicht je Person',
      legalBasis: 'Art. 6 Abs. 1 lit. b DSGVO, Art. 88 DSGVO i. V. m. § 26 BDSG',
      // People taking part and creators are account IDs; people without an account have a name
      // and optionally an e-mail address; title and description are free text; an address can be
      // the home of a private client.
      categories: ['account-id', 'name', 'email', 'free-text', 'address'],
      // Proposal: three years after the appointment (evidence of visits, § 195 BGB). Still to be
      // confirmed with the DPO.
      retention: { period: 'P3Y', after: 'appointment' },
      erase: 'anonymize',
    },
    {
      entity: 'appointments.request',
      purpose:
        'Terminanfragen aus E-Mails an die Adresse der Firma (ADR-0061): Absender, Betreff und Text der Mail und der daraus gelesene Terminvorschlag, bis jemand die Anfrage übernimmt oder ablehnt; Auslesen mit KI, wenn die Mail keinen Kalendereintrag enthält',
      legalBasis: 'Art. 6 Abs. 1 lit. b und f DSGVO',
      // Sender address and name; subject and text are free text and can name third parties.
      categories: ['account-id', 'name', 'email', 'free-text'],
      // Requests nobody answered are no longer of use after a month; decided ones keep only the
      // subject and the link to the appointment until then.
      retention: { period: 'P30D', after: 'received' },
      erase: 'delete',
      processors: ['ai-provider'],
    },
  ],
});
